import { useState } from 'react';
import * as api from '../api/writtenContribution';

// Define the TypeScript interface for a contribution
export interface Author {
  _id: string;
  fullName: string;
  email: string;
}

export interface Comment {
  _id: string;
  author: Author | string;
  authorDisplayName?: string;
  text: string;
  createdAt: string;
  updatedAt?: string;
}

export interface WrittenContribution {
  _id: string;
  author: Author | { _id: string } | null;
  authorDisplayName?: string;
  title: string;
  fileUrl: string;
  publicId: string;
  mimeType: string;
  fileSizeBytes: number;
  // NEW fields for cover
  publicationCover: string;
  publicationCoverPublicId?: string;
  publicationCoverMimeType?: string;
  publicationCoverSizeBytes?: number;
  summary?: string;
  summaryEditedByAdmin?: boolean;
  isDownloadable: boolean;
  commentCount: number;
  viewCount: number;
  downloadCount: number;
  comments: Comment[];
  slug: string;
  createdAt: string;
  updatedAt: string;
}

// Hook
export function useWrittenContributions(token?: string) {
  const [contributions, setContributions] = useState<WrittenContribution[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all contributions
  const loadContributions = async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) {
        setLoading(true);
      }
      const data: WrittenContribution[] = await api.callFetchContributions();
      setContributions(data);
    } catch (err: any) {
      setError(err.message || 'Error loading contributions');
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  };

  // Add new contribution
  const addNewContribution = async (data: {
    title: string;
    fileUrl: string;
    publicId: string;
    mimeType: string;
    fileSizeBytes: number;
    summary?: string;
    isDownloadable?: boolean;
    publicationCover: string;
    publicationCoverPublicId?: string;
    publicationCoverMimeType?: string;
    publicationCoverSizeBytes?: number;
  }) => {
    if (!token) throw new Error('User not authenticated');
    const contribution: WrittenContribution = await api.callCreateContribution(data, token);
    setContributions(prev => [contribution, ...prev]);
    return contribution;
  };

  return {
    contributions,
    loading,
    error,
    loadContributions,
    addNewContribution,
  };
}
